import xhrService, { isRequestCancelled } from './xhrService';
import { env } from '../config';

/**
 * SW2-2394 — a rolling timeline of what the app just did, attached to a
 * Usersnap report so a bug arrives with its own evidence.
 *
 * Records three things, interleaved in one list:
 *
 * - **Failed API calls.** The endpoint and status separate failures that share
 *   a message. "Inspection not found" is the standing example: it is produced
 *   by several different problems and means access resolution failed, not that
 *   a record is missing.
 * - **Successful mutations.** A write that returns 200 and changes nothing
 *   reads, to a failures-only buffer, as "nothing happened" — the opposite of
 *   the truth. Recording writes whatever their status also dates the change:
 *   the save that preceded a crash is usually what caused it.
 * - **Uncaught JavaScript errors.** A failure in an event handler or an async
 *   callback produces no rejected request at all, so without these it leaves no
 *   trace. Usersnap captures the console too, but only behind an authenticated
 *   link, which an automated triage reader cannot follow.
 *
 *   Render and lifecycle errors are NOT among them: `AppErrorBoundary` wraps
 *   the app, and React 17's production build handles a boundary-caught error
 *   with a plain try/catch without re-dispatching it to `window`. It does
 *   re-dispatch in development, so this listener looks more complete locally
 *   than it is. Covering that class means recording from `componentDidCatch`.
 *
 * One list rather than three, because the ordering between them is most of the
 * diagnosis: the write that preceded a crash is usually the cause of it.
 *
 * **Inert unless the feedback widget is enabled.** Nothing is recorded, no
 * listeners are installed and nothing is written to storage when
 * `REACT_APP_FEATURE_USER_FEEDBACK_WIDGET` is off — which is every environment
 * today. There is no reason to hold a user's endpoint paths and correlation ids
 * when they have no widget to submit them with.
 *
 * Registered as an import side-effect, the same as
 * `trialRestrictionInterceptor`, so `xhrService.js` stays untouched.
 *
 * **Import this before `trialRestrictionInterceptor`.** Axios runs response
 * interceptors in registration order, and that one deliberately swallows a
 * trial-restricted 403 by returning a promise that never settles. Registered
 * after it, this buffer would never see those rejections — and a user hitting
 * the read-only wall is exactly the sort of person who then files a report.
 */

interface ActivityEntry {
  at: number;
  text: string;
  /** Kept apart from `text` so it can be dropped from older entries on read. */
  requestId?: string | null;
}

/** Enough history to show a sequence; the report itself is trimmed to fit. */
const CAPACITY = 8;

/**
 * How many of the newest entries carry their correlation id.
 *
 * A UUID costs about forty characters — nearly half a line — against a Slack
 * block that truncates. It earns that on the failure being reported, and very
 * little on the one before last, which nobody looks up.
 */
const REQUEST_IDS_SHOWN = 2;

/** Longest URL kept. Long enough for a nested route, short enough to stay readable. */
const MAX_URL = 120;

/**
 * Error text is truncated hard. A message can quote a record, a token or a
 * response body, and this travels to Slack and to whatever reads it there.
 */
const MAX_ERROR = 140;

/** A UUID is 36; anything longer is a backend that changed its mind. */
const MAX_REQUEST_ID = 48;

/** Writes. A failed read is worth recording; a successful one is just noise. */
const MUTATIONS = ['POST', 'PUT', 'PATCH', 'DELETE'];

/**
 * The buffer survives a reload in `sessionStorage`.
 *
 * When something breaks, people refresh and *then* report it. In memory alone
 * that refresh wipes precisely the evidence the report exists to carry, and the
 * report arrives with an empty timeline looking as though nothing went wrong.
 *
 * Session, not local, so it does not accumulate across the browser or outlive
 * the session. Not strictly per-tab, though: duplicating a tab, or reopening a
 * closed one, copies `sessionStorage` into the new context, so a restored
 * timeline may have come from the tab this one was duplicated from.
 */
const STORAGE_KEY = 'sw2.feedback.activity';

/**
 * Restored entries older than this are dropped. A report should carry the
 * problem in front of the reporter, not everything that happened this morning.
 */
const MAX_RESTORE_AGE_MS = 10 * 60 * 1000;

const RELOAD_MARKER = 'page reloaded';

/**
 * Whether to record at all. Read once at module load, which is safe: this is a
 * `REACT_APP_*` value and those are baked in at build time, not read at runtime.
 */
const ENABLED = env.REACT_APP_FEATURE_USER_FEEDBACK_WIDGET === 'true';

const buffer: ActivityEntry[] = [];

/**
 * Which sign-in a request belongs to.
 *
 * Bumped at logout. Every request is stamped with the value current when it was
 * sent, and a response whose stamp no longer matches is dropped — so a request
 * in flight when a user signs out cannot land afterwards and put that user's
 * path and correlation id back into shared sessionStorage.
 *
 * A counter rather than a one-way "stopped" flag, because not every logout ends
 * the page: `PublicRoute` calls `logout()` when an already-authenticated user
 * visits /login, and the next sign-in continues in the same SPA instance. A
 * permanent flag would leave that tab recording nothing for the rest of the
 * session; a counter simply means the next user's requests carry the next
 * value.
 */
let generation = 0;

/** The generation a request was sent under, read back when it settles. */
const GENERATION_KEY = '__sw2ActivityGeneration';

/**
 * Every read and write is guarded. `sessionStorage` throws on access in a
 * private window, with site data blocked, and inside some sandboxed frames —
 * and a feedback buffer must never be the thing that breaks the page.
 */
function persist() {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(buffer));
  } catch {
    // Unavailable or over quota. The in-memory buffer still works.
  }
}

function record(text: string, requestId: string | null = null) {
  // Guarded here as well as at the registration sites below, so the exported
  // `recordCrash` is inert too without every caller having to know.
  if (!ENABLED) {
    return;
  }

  buffer.push({ at: Date.now(), text, requestId });

  while (buffer.length > CAPACITY) {
    buffer.shift();
  }

  persist();
}

function restore() {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return;
    }

    const parsed = JSON.parse(raw);

    if (!Array.isArray(parsed)) {
      return;
    }

    const cutoff = Date.now() - MAX_RESTORE_AGE_MS;
    const fresh = parsed
      .filter(
        (entry: any) =>
          entry &&
          typeof entry.at === 'number' &&
          typeof entry.text === 'string' &&
          entry.at >= cutoff,
      )
      .slice(-CAPACITY);

    if (!fresh.length) {
      return;
    }

    buffer.push(...fresh);

    // Mark the break explicitly. Without it a failure from before the refresh
    // and one from after read as a single uninterrupted sequence, which would
    // mislead whoever is reconstructing what happened.
    //
    // Collapsed when the newest restored entry is already a marker: the error
    // screen offers a Reload button, so repeated reloads are expected, and one
    // slot per reload would leave a timeline of nothing but markers in exactly
    // the case this feature targets.
    if (fresh[fresh.length - 1]?.text !== RELOAD_MARKER) {
      record(RELOAD_MARKER);
    }
  } catch {
    // Unavailable, or something else wrote nonsense under this key.
  }
}

/**
 * Path only.
 *
 * The origin is constant and would just consume the size budget. The query
 * string is dropped for a different reason: this text reaches Usersnap, Slack
 * and whatever reads it there, and a parameter can carry an email, a token or a
 * search term.
 *
 * That strip is active, not precautionary — seven call sites build a query
 * string into the path (`api/user.js`, `api/project.ts`, `api/tenant.ts` and
 * `api/inspection.js` among them). Callers passing axios `params` instead are
 * already safe: those are serialised at send time and never written back to
 * `config.url`.
 */
function toPath(rawUrl: string): string {
  const withoutOrigin = rawUrl
    .replace(/^https?:\/\/[^/]+/i, '')
    .split(/[?#]/)[0];

  return withoutOrigin.length > MAX_URL
    ? `${withoutOrigin.slice(0, MAX_URL)}…`
    : withoutOrigin;
}

/**
 * Patterns worth removing from error text before it leaves the app.
 *
 * Truncation is not redaction — it just sends the first `MAX_ERROR`
 * characters, which is exactly where a token in a thrown message would sit.
 * These run before the cut so a secret cannot be half-sent either.
 *
 * Deliberately shape-based and short. The aim is the common accidents — a JWT
 * or key echoed into a message, an address in a validation error — not a
 * guarantee. Anything that must never leave has no business in an error string
 * in the first place; record paths and ids stay, because they are the
 * diagnosis.
 */
const REDACTIONS: Array<[RegExp, string]> = [
  // JWTs, which carry a session in three base64 segments.
  [/eyJ[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]+/g, '[jwt]'],
  [/\bBearer\s+[A-Za-z0-9._~+/-]+=*/gi, 'Bearer [token]'],
  // Long unbroken runs: keys, hashes, base64 blobs. Above any word or id.
  [/\b[A-Za-z0-9+/_-]{40,}={0,2}\b/g, '[redacted]'],
  [/\b[0-9a-f]{32,}\b/gi, '[hash]'],
  [/[\w.+-]+@[\w-]+\.[\w.-]+/g, '[email]'],
];

function scrub(value: string): string {
  return REDACTIONS.reduce(
    (text, [pattern, replacement]) => text.replace(pattern, replacement),
    value,
  );
}

function truncate(value: string, max: number): string {
  const flat = scrub(value.replace(/\s+/g, ' ').trim());

  return flat.length > max ? `${flat.slice(0, max)}…` : flat;
}

/**
 * The correlation id tying a report to the server-side log for that exact
 * request. Both spellings are accepted because the backend is inconsistent.
 *
 * Optional by design: the change that emits it is on dev and not yet in
 * production, so this reads as absent against a deployed backend today and
 * begins populating with the next release.
 */
function readRequestId(data: any): string | null {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const id = data.requestId || data.request_id;

  // Capped: `text` is bounded by MAX_URL/MAX_ERROR but this is not, and
  // `fitToBudget` keeps the newest entries — so one oversized id on the newest
  // line survives every trim and can leave the report with no timeline at all.
  return typeof id === 'string' ? id.slice(0, MAX_REQUEST_ID) : null;
}

if (ENABLED) {
  // Stamp on the way out so the generation can be checked on the way back. A
  // separate registration rather than a change to `xhrService.js`, which this
  // module deliberately leaves untouched.
  //
  // Axios runs request interceptors asynchronously, so a request begun in the
  // very same tick as a logout would be stamped with the new generation. That
  // ordering does not occur in practice — logout is a user action, and by then
  // anything in flight has long since been stamped — but it is the boundary of
  // what this can promise.
  xhrService.interceptors.request.use((config) => {
    try {
      (config as any)[GENERATION_KEY] = generation;
    } catch {
      // Frozen config; the response side treats a missing stamp as current.
    }

    return config;
  });

  xhrService.interceptors.response.use(
    (response) => {
    // Recording is wrapped and the return sits outside it. A throw in here
    // would otherwise be converted by axios into a rejection, so a POST that
    // saved an inspection on the server would reach the caller as a failure.
    // Telemetry must not alter the chain it observes.
    try {
      const method = (response?.config?.method || 'get').toUpperCase();

      if (
        MUTATIONS.indexOf(method) !== -1 &&
        isCurrentGeneration(response?.config)
      ) {
        record(
          `${method} ${toPath(response?.config?.url || '(unknown)')} · ${
            response?.status
          }`,
        );
      }
    } catch {
      // The report loses one line; the request is unaffected.
    }

    return response;
  },
  (error) => {
    // Same reasoning as the fulfilled handler: a throw here would replace the
    // rejection value, breaking every downstream `error.response.data.message`
    // read and showing the user a telemetry TypeError instead of the backend's
    // message.
    try {
      // A request this client abandoned is not a failure worth reporting; it is
      // usually a component unmounting or a superseded search. A request from a
      // previous sign-in is not this user's to report.
      if (!isRequestCancelled(error) && isCurrentGeneration(error?.config)) {
        const method = (error?.config?.method || 'get').toUpperCase();
        // No response at all means the request never landed — offline, DNS,
        // CORS or a timeout. That is a different bug from an HTTP error, and
        // for an app used in the field it is often the bug itself.
        const status = error?.response?.status ?? 'no response';

        record(
          `${method} ${toPath(error?.config?.url || '(unknown)')} · ${status}`,
          readRequestId(error?.response?.data),
        );
      }
    } catch {
      // As above: the report loses one line, the rejection is untouched.
    }

      return Promise.reject(error);
    },
  );
}

/**
 * Uncaught errors and rejected promises. Registered once at import; the app has
 * no other global handler, so nothing is being overridden here.
 */
if (ENABLED && typeof window !== 'undefined') {
  restore();

  // Both listeners keep their whole body inside a guard. The rule is not
  // "guard the reads that look dangerous" — three rounds of review went on
  // deciding which property accesses were safe and being wrong each time. Every
  // read here is on an object this app did not construct, so none of them are
  // judged individually any more. A throw that escapes either listener is
  // especially unhelpful: it fires an 'error' event, which lands in the other
  // one, and the telemetry's own crash gets recorded as the application's.
  window.addEventListener('error', (event: ErrorEvent) => {
    try {
      // A failed <img>/<script> load also fires this, with no `error` object
      // and no message worth reporting. Those are noise, not crashes.
      // "Script error." is what a cross-origin script (Usersnap's own bundle,
      // analytics) reports: no message, no file, no line. Content-free, and it
      // would evict a real entry from an eight-slot buffer.
      if (!event?.message || event.message === 'Script error.') {
        return;
      }

      const where = event.filename
        ? ` · ${toPath(event.filename)}:${event.lineno || 0}`
        : '';

      record(`JS ${truncate(event.message, MAX_ERROR)}${where}`);
    } catch {
      // Unreadable event. Nothing to record, and nothing thrown onwards.
    }
  });

  window.addEventListener(
    'unhandledrejection',
    (event: PromiseRejectionEvent) => {
      // Nothing above the try touches the event — `event.reason` is a property
      // access like any other. Three shapes found by successive review passes,
      // each of which escaped a guard drawn one line too tight:
      // `{ config: {}, get response() { throw } }`, reached by the axios shape
      // check; `{ message: Object.create(null) }`, which reads fine and throws
      // only on conversion; and an event whose `reason` getter throws.
      let message = 'unknown';

      try {
        const reason: any = event?.reason;

        // An axios rejection nobody caught is already in the buffer from the
        // interceptor; recording it again would burn two of the eight slots on
        // one event and evict the write that preceded it.
        //
        // Matched on shape, not `isAxiosError`: that property only arrived in
        // axios 0.19, and the shape check means the same thing on 0.18 and 1.x
        // (SW2-2170). `offlineFallback.ts` keys on the same shape.
        if (reason?.config && (reason.response || reason.request)) {
          return;
        }

        const raw =
          reason &&
          (reason.message ||
            (typeof reason.toString === 'function' ? reason.toString() : ''));

        message = raw ? String(raw) : 'unknown';
      } catch {
        // Could not read it. Recorded generically rather than dropped — a
        // reason we cannot inspect is still an unhandled rejection, and saying
        // "unknown" is more honest than saying nothing happened.
      }

      record(`UNHANDLED ${truncate(message, MAX_ERROR)}`);
    },
  );
}

/** Whole seconds are enough; the useful signal is ordering and rough recency. */
function relativeAge(at: number, now: number): string {
  const seconds = Math.max(0, Math.round((now - at) / 1000));

  return seconds < 60 ? `${seconds}s ago` : `${Math.round(seconds / 60)}m ago`;
}

/**
 * The timeline as one line per event, newest first — what happened last is most
 * likely to explain the report.
 *
 * One string per event rather than an object, because Usersnap pretty-prints
 * custom data: a nested object costs roughly six lines to say what one line
 * says, which pushes the oldest events past Slack's truncation.
 */
export function getRecentActivity(now: number = Date.now()): string[] {
  return buffer
    .slice()
    .reverse()
    .map((entry, index) => {
      const line = `${relativeAge(entry.at, now)} · ${entry.text}`;

      return entry.requestId && index < REQUEST_IDS_SHOWN
        ? `${line} · req ${entry.requestId}`
        : line;
    });
}

/**
 * Record a render or lifecycle crash caught by `AppErrorBoundary`.
 *
 * The window `'error'` listener above cannot see these: React 17's production
 * build handles a boundary-caught error with a plain try/catch and does not
 * re-dispatch it. Without this call the white-screen case — the most severe
 * thing a user can report — reaches the report with no evidence at all.
 */
export function recordCrash(message: string) {
  record(`CRASH ${truncate(message, MAX_ERROR)}`);
}

/** A response is dropped when its request predates the current sign-in. */
function isCurrentGeneration(config: any): boolean {
  const stamped = config?.[GENERATION_KEY];

  // Unstamped means it never passed the request interceptor — treat it as
  // current rather than silently dropping evidence.
  return stamped === undefined || stamped === generation;
}

/**
 * Drop everything held, and disown anything still in flight.
 *
 * Called on logout. Sign-out is in-tab here — no reload, no storage clear — so
 * without this the next user on the same device files a report carrying the
 * previous user's endpoint paths, which contain record ids, and their server
 * correlation ids. These are shared field tablets, and the logout path already
 * clears cached offline data for exactly this reason.
 *
 * Clearing alone is not enough: requests in flight at sign-out settle after it
 * and would repopulate storage. The generation is bumped first, so those
 * responses are dropped when they arrive, then the buffer is emptied.
 */
export function clearForLogout() {
  generation += 1;
  clearRecentActivity();
}

/** Empty the buffer without stopping recording. Exported for tests. */
export function clearRecentActivity() {
  buffer.length = 0;

  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Unavailable; the in-memory clear above is what matters.
  }
}
